import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { FormHeaderVisual } from "@/components/sections/ContactVisuals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderButtonContent = (loading, done) => {
  if (loading) {
    return (
      <>
        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
      </>
    );
  }
  if (done) {
    return (
      <>
        <CheckCircle2 className="w-4 h-4" /> Sent
      </>
    );
  }
  return (
    <>
      Send Message <Send className="w-4 h-4" />
    </>
  );
};

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message saved! Opening WhatsApp…");
      if (data?.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => {
        setForm({ name: "", email: "", message: "" });
        setDone(false);
      }, 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (Array.isArray(detail) && detail[0]?.msg) ||
        (typeof detail === "string" ? detail : null) ||
        "Could not submit. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-form-section" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 reveal">
            <div className="text-xs font-mono-d uppercase tracking-[0.28em] text-[#0066ff] mb-3">
              / 06 — Project Brief
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight text-[#0a0e1a]">
              Tell us what you’re <span className="text-grad">building</span>.
            </h2>
            <p className="mt-4 text-[#475569]">
              Share your idea — timelines, scope, references. We respond within hours and route
              the message straight to our WhatsApp inbox.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-[#1f2937]">
              {["Free 30-min discovery call", "Quote within 24 hours", "NDA on request"].map((t) => (
                <li key={t} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#10b981]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="md:col-span-7 surface-strong rounded-2xl p-6 sm:p-8 reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <FormHeaderVisual />
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="field">
                <input
                  data-testid="contact-input-name"
                  id="cf-name"
                  type="text"
                  placeholder=" "
                  value={form.name}
                  onChange={update("name")}
                  autoComplete="name"
                  required
                />
                <label htmlFor="cf-name">Your Name</label>
              </div>
              <div className="field">
                <input
                  data-testid="contact-input-email"
                  id="cf-email"
                  type="email"
                  placeholder=" "
                  value={form.email}
                  onChange={update("email")}
                  autoComplete="email"
                  required
                />
                <label htmlFor="cf-email">Email Address</label>
              </div>
            </div>
            <div className="field mt-4">
              <textarea
                data-testid="contact-input-message"
                id="cf-message"
                placeholder=" "
                value={form.message}
                onChange={update("message")}
                rows={5}
                required
              />
              <label htmlFor="cf-message">Project Details</label>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="text-xs text-[#6b7280]">
                Submission opens WhatsApp with your message pre-filled.
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading || done}
                className="btn-primary disabled:opacity-70"
              >
                {renderButtonContent(loading, done)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
